import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, X, Sparkles } from "lucide-react";
import { postAiClarify } from "@/lib/api";
import type { Lang } from "@/lib/types";

interface HelpMeButtonProps {
  lang: Lang;
  questionId: string;
  questionLabel: string;
  options: { value: string; label: string }[];
  onSuggestionPicked: (value: string) => void;
}

export function HelpMeButton({
  lang,
  questionId,
  questionLabel,
  options,
  onSuggestionPicked,
}: HelpMeButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<{ value: string; followUp: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPhrase("");
    setSuggestion(null);
    setError(null);
    setLoading(false);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const handleSubmit = async () => {
    if (!phrase.trim()) return;
    setLoading(true);
    setError(null);
    setSuggestion(null);
    const res = await postAiClarify({
      lang,
      question_id: questionId,
      question_label: questionLabel,
      options,
      user_phrase: phrase,
    });
    setLoading(false);
    if ("error" in res) {
      setError(res.error);
      return;
    }
    setSuggestion({
      value: res.suggested_choice ?? "",
      followUp: res.follow_up_question,
    });
  };

  const handleUseSuggestion = () => {
    if (suggestion?.value) {
      onSuggestionPicked(suggestion.value);
      close();
    }
  };

  // Lock body scroll when sheet/modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const sheetContent = (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles size={20} strokeWidth={1.75} className="text-signal" />
          <h3 className="font-display text-h3">{t("wizard.help_me_modal.title")}</h3>
        </div>
        <button
          type="button"
          onClick={close}
          className="flex h-9 w-9 items-center justify-center rounded-pill text-muted transition-colors hover:bg-ink/5 hover:text-ink"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>

      <textarea
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder={t("wizard.help_me_modal.placeholder")}
        className="input min-h-28 resize-none"
        disabled={loading}
        autoFocus
      />

      {error && (
        <p className="mt-3 text-caption text-danger">
          {t("errors.submit_failed")} ({error})
        </p>
      )}

      {suggestion && suggestion.value && (
        <div className="ai-suggestion-card mt-4">
          <p className="mb-2 text-caption text-muted">{t("wizard.help_me_modal.suggested")}</p>
          <p className="font-body text-body text-ink">
            {options.find((o) => o.value === suggestion.value)?.label ?? suggestion.value}
          </p>
          {suggestion.followUp && (
            <p className="mt-2 text-caption text-ink-soft">{suggestion.followUp}</p>
          )}
          <button
            type="button"
            onClick={handleUseSuggestion}
            className="btn-primary mt-4 w-full sm:w-auto"
          >
            {t("wizard.help_me_modal.use_suggestion")}
          </button>
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="button"
          onClick={close}
          className="btn-secondary sm:px-5 sm:py-2.5 sm:min-h-[unset]"
        >
          {t("wizard.help_me_modal.cancel")}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !phrase.trim()}
          className="btn-primary"
        >
          {loading ? t("wizard.help_me_modal.loading") : t("wizard.help_me_modal.submit")}
        </button>
      </div>
    </>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-caption text-muted transition-colors hover:bg-ink/5 hover:text-ink"
      >
        <HelpCircle size={16} strokeWidth={1.75} />
        {t("wizard.help_me")}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-ink/40"
              onClick={close}
              aria-hidden
            />

            {/* Mobile: bottom sheet sliding up. Desktop: centered dialog. */}
            <motion.div
              key="sheet"
              role="dialog"
              aria-modal="true"
              initial={{ y: "100%", opacity: 0.6 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0.6 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="bottom-sheet"
            >
              {/* Drag handle visible on mobile only */}
              <div className="mx-auto mb-3 h-1 w-10 rounded-pill bg-border md:hidden" />
              {sheetContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
